import axios from "axios";

const userServiceRoot = process.env.REACT_APP_USER_SERVICE;

/**
 * Flexible context details that vary based on event type
 */
export interface UserEventContext {
  /**
   * Workspace ID (for workspace-related events)
   */
  workspaceId?: string;

  /**
   * Dashboard ID (for dashboard-related events)
   */
  dashboardId?: string;

  /**
   * Project ID (for project-related events)
   */
  projectId?: string;

  /**
   * Bulk delete counts for shapefile management events
   */
  bulkDeleteCounts?: {
    shapefileIds: number;
    projectShapefileIds: number;
    folderIds: number;
    orgFolderIds: number;
    systemFolderIds: number;
  };

  /**
   * Manage mode action type
   */
  manageModeAction?: "button-click" | "context-menu";

  /**
   * Context menu action used
   */
  contextMenuAction?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * Request model for creating a user event
 */
export interface CreateUserEventRequest {
  eventType: string;
  context?: UserEventContext;
}

/**
 * Creates a user event via the API
 * This function will not throw errors to prevent user experience disruption
 * @param eventData The event data to send
 * @returns Promise that resolves regardless of success/failure
 */
export async function createUserEvent(eventData: CreateUserEventRequest): Promise<void> {
  try {
    await axios.post(`${userServiceRoot}/userevent`, eventData, {
      withCredentials: true
    });
  } catch (error) {
    // Silently handle errors - user events should not disrupt user experience
    // Only log in development for debugging
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("Failed to send user event:", error);
    }
  }
}

/**
 * Event types for user analytics
 */
export enum UserEventType {
  Screenshot = "Screenshot Taken",
  ExportChartData = "Chart Data Exported",
  CopyDataTable = "Data Table Data Copied",
  ExportDataTable = "Data Table Data Exported",
  ExportForecasts = "Forecasts Exported",
  GroupBy = "Groupby Change from Activity",
  ChartAxisFieldSelector = "Chart Axis Field Selected",
  ChartFocus = "Chart Focus Set",
  ChartDataSource = "Chart Data Source Set",
  ChartTimeStep = "Chart Time Step Set",
  ChartGlobalGroupByChange = "Chart Groupby Set",
  ChartProduct = "Chart Product Set",
  ChartOpened = "Chart is Displayed",
  WellTicketTab = "Well/Facility Ticket Tab Changed",
  WellCardViewed = "Well Card Viewed",
  MidstreamNavigatorButtonToggled = "Midstream Navigator Button Toggled",
  SalesFacilitiesFilter = "Sales Facilities Filtered From Well",
  WellNetworkMapLayer = "Well Network Map Layer Shown",
  ManageModeEntered = "Manage Mode Entered",
  ShapefileBulkDelete = "Shapefile Bulk Delete",
  ManageModeContextMenu = "Manage Mode Context Menu"
}
